import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  List,
  ListItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { ODI3Result, SpO2Metrics, V2ExamineeInfo } from "../../commons";
import { SectionTitle } from "./commons";

interface SASRiskContent1Props {
  examinee: V2ExamineeInfo;
  odi3Result: ODI3Result;
  spO2Metrics: SpO2Metrics;
}
export default function SASRiskContent1({
  examinee,
  odi3Result,
  spO2Metrics,
}: SASRiskContent1Props) {
  return (
    <>
      <Stack sx={{ gap: "24px" }}>
        <SectionTitle text="判定" />
        <JudgementContent odi3Result={odi3Result} />
      </Stack>
      <Stack sx={{ gap: "32px" }}>
        <Stack direction="row" sx={{ justifyContent: "space-between" }}>
          <SectionTitle text="ODI3の結果" />
          <Typography sx={{ fontSize: "10px", fontWeight: 400 }}>
            測定日 : {odi3Result.measurement_date}
          </Typography>
        </Stack>
        <ODI3Content spO2Metrics={spO2Metrics} />
      </Stack>
      <Stack sx={{ gap: "24px" }}>
        <SectionTitle text="その他検査結果と身体情報" />
        <OtherContent examinee={examinee} spO2Metrics={spO2Metrics} />
      </Stack>
    </>
  );
}

interface JudgementContentProps {
  odi3Result: ODI3Result;
}
function JudgementContent({ odi3Result }: JudgementContentProps) {
  const judgementListItems = [
    ["A", "ODI3 1未満", "睡眠呼吸障害のリスクは非常に低いです"],
    ["B", "1 ≦ ODI3 ＜ 5", "睡眠呼吸障害のリスクは低いです"],
    ["C", "5 ≦ ODI3 ＜ 15", "睡眠呼吸障害のリスクが中程度です"],
    ["D", "15 ≦ ODI3 ＜ 30", "睡眠呼吸障害のリスクが高いです"],
    ["E", "ODI3 30以上", "睡眠呼吸障害のリスクが非常に高いです"],
    ["F", "-", "測定条件不良のための判定不能"],
  ];
  return (
    <Stack direction="row" sx={{ ml: "8px" }}>
      <Stack
        direction="row"
        sx={{
          justifyContent: "center",
          alignItems: "baseline",
          gap: "8px",
          border: "1px solid #000",
          height: "100px",
          width: "238px",
        }}
      >
        <Typography sx={{ fontSize: "40px", fontWeight: 700, mt: "12px" }}>
          {odi3Result.judgement}
        </Typography>
        <Typography sx={{ fontSize: "13px", fontWeight: 700 }}>判定</Typography>
      </Stack>
      <Stack className="judgementList" sx={{ ml: "8px" }}>
        <List sx={{ gap: "8px", px: "8px", py: 0 }}>
          {judgementListItems.map((item, idx) => (
            <ListItem
              key={idx}
              sx={{
                p: 0,
                gap: "8px",
                "& .MuiTypography-root": { fontSize: "10px", fontWeight: 400 },
              }}
            >
              <Typography component="span">{item[0]} :</Typography>
              <Typography component="span" sx={{ ml: "4px", width: "80px" }}>
                {item[1]}
              </Typography>
              <Typography component="span" sx={{ ml: "4px" }}>
                {item[2]}
              </Typography>
            </ListItem>
          ))}
        </List>
      </Stack>
    </Stack>
  );
}

interface ODI3CountLabelProps {
  scoreString: string;
  isWarning: boolean;
  /** 0 から 100 まで */
  position: number;
}
function ODI3CountLabel({
  scoreString,
  isWarning,
  position,
}: ODI3CountLabelProps) {
  if (position > 100 || position < 0) {
    return <></>;
  }
  return (
    <Stack
      sx={{
        width: "fit-content",
        transform: "translateX(-50%)",
        position: "absolute",
        height: "55px",
        top: "-50px",
        zIndex: 1,
        left: `${position}%`,
      }}
    >
      <Stack
        direction="row"
        sx={{
          height: "42px",
          alignItems: "baseline",
          "& p": {
            color: isWarning ? "error.main" : "text.primary",
          },
        }}
      >
        <Typography sx={{ fontSize: "26px", fontWeight: 500, height: "26px" }}>
          {scoreString}
        </Typography>
        <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>回</Typography>
      </Stack>
      <PlayArrowIcon
        sx={{
          rotate: "90deg",
          width: "16px",
          height: "16px",
          alignSelf: "center",
          filter:
            "drop-shadow(0 0 1px white) drop-shadow(0 0 1px white) drop-shadow(0 0 1px white)",
        }}
      />
    </Stack>
  );
}

interface ODI3ContentProps {
  spO2Metrics: SpO2Metrics;
}
function ODI3Content({ spO2Metrics }: ODI3ContentProps) {
  const odi3Score = spO2Metrics.odi3;

  const chartLabelStyle = {
    fontSize: "13px",
    fontWeight: 500,
    m: "auto",
    color: "black.100",
  };

  return (
    <Stack sx={{ position: "relative" }}>
      <Stack
        sx={{
          width: "98%",
          ml: "8px",
          mt: "50px",
        }}
      >
        <Stack
          direction="row"
          sx={{
            "& > div:nth-of-type(1)": { borderRadius: "4px 0 0 4px" },
            "& > div:nth-of-type(4)": { borderRadius: "0 4px 4px 0" },
          }}
        >
          <Stack
            sx={{
              width: "calc(100% / 7)",
              backgroundColor: "#0056A080",
              height: "32px",
              position: "relative",
            }}
          >
            {odi3Score == null && (
              <ODI3CountLabel
                scoreString="-- "
                isWarning={false}
                position={0}
              />
            )}
            {odi3Score != null && odi3Score < 5 && (
              <ODI3CountLabel
                scoreString={`${odi3Score}`}
                isWarning={false}
                position={(odi3Score / 5) * 100}
              />
            )}
            <Typography sx={chartLabelStyle}>低い</Typography>
          </Stack>
          <Stack
            sx={{
              width: "calc((100% / 7) * 2)",
              backgroundColor: "#FFCE80",
              height: "32px",
              position: "relative",
            }}
          >
            {odi3Score != null && odi3Score >= 5 && odi3Score < 15 && (
              <ODI3CountLabel
                scoreString={`${odi3Score}`}
                isWarning={false}
                position={((odi3Score - 5) / 10) * 100}
              />
            )}
            <Typography sx={chartLabelStyle}>中</Typography>
          </Stack>
          <Stack
            sx={{
              width: "calc((100% / 7) * 3)",
              backgroundColor: "#FF3A3080",
              height: "32px",
              position: "relative",
            }}
          >
            {odi3Score != null && odi3Score >= 15 && odi3Score < 30 && (
              <ODI3CountLabel
                scoreString={`${odi3Score}`}
                isWarning={false}
                position={((odi3Score - 15) / 15) * 100}
              />
            )}
            <Typography sx={chartLabelStyle}>高い</Typography>
          </Stack>
          <Stack
            sx={{
              width: "calc(100% / 7)",
              backgroundColor: "#FF756E",
              height: "32px",
              position: "relative",
            }}
          >
            {odi3Score != null && odi3Score >= 30 && (
              <ODI3CountLabel
                scoreString={`${odi3Score}`}
                isWarning={false}
                position={(((odi3Score >= 34 ? 34 : odi3Score) - 30) / 4) * 100}
              />
            )}
            <Typography sx={chartLabelStyle}>非常に高い</Typography>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          sx={{
            "& p": {
              fontSize: "13px",
              fontWeight: 500,
            },
          }}
        >
          <Stack sx={{ width: "calc(100% / 7)" }}>
            <Typography>0</Typography>
          </Stack>
          <Stack sx={{ width: "calc((100% / 7) * 2)" }}>
            <Typography>5</Typography>
          </Stack>
          <Stack sx={{ width: "calc((100% / 7) * 3)" }}>
            <Typography>15</Typography>
          </Stack>
          <Stack sx={{ width: "calc(100% / 7)" }}>
            <Typography>30〜</Typography>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}

interface OtherContentProps {
  examinee: V2ExamineeInfo;
  spO2Metrics: SpO2Metrics;
}
function OtherContent({ examinee, spO2Metrics }: OtherContentProps) {
  const labelStyle = {
    fontSize: "11.5px",
    fontWeight: 500,
    color: "#707070",
  };
  const cellStyle = {
    border: "solid #000",
    borderWidth: "0px 1px 1px 0px !important",
    color: "#000",
    fontSize: "10px",
    fontWeight: "400",
    height: "36px",
    p: 0,
    pl: "8px",
    width: "122px",
  };
  const titleCellStyle = {
    ...cellStyle,
    fontWeight: "500",
    width: "120px",
    backgroundColor: "#EAEAEA80",
  };
  return (
    <Stack gap={3}>
      <Stack direction="row" gap={2} sx={{ ml: "8px" }}>
        <Stack gap={2}>
          <Typography sx={labelStyle}>検査結果</Typography>
          <TableContainer
            sx={{
              border: "solid #000",
              borderWidth: "1px 0px 0px 1px",
              borderRadius: 0,
            }}
          >
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={titleCellStyle}>ODI2</TableCell>
                  <TableCell sx={cellStyle}>
                    {spO2Metrics.odi2 == null ? "-" : spO2Metrics.odi2}
                  </TableCell>
                  <TableCell sx={titleCellStyle}>SpO2平均値 (％)</TableCell>
                  <TableCell sx={cellStyle}>
                    {spO2Metrics.mean == null ? "-" : spO2Metrics.mean}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={titleCellStyle}>ODI3</TableCell>
                  <TableCell sx={cellStyle}>
                    {spO2Metrics.odi3 == null ? "-" : spO2Metrics.odi3}
                  </TableCell>
                  <TableCell sx={titleCellStyle}>Dip最低値 (％)</TableCell>
                  <TableCell sx={cellStyle}>
                    {spO2Metrics.minimum == null ? "-" : spO2Metrics.minimum}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={titleCellStyle}>ODI4</TableCell>
                  <TableCell sx={cellStyle}>
                    {spO2Metrics.odi4 == null ? "-" : spO2Metrics.odi4}
                  </TableCell>
                  <TableCell sx={titleCellStyle}>睡眠時間</TableCell>
                  <TableCell sx={cellStyle}>
                    {spO2Metrics.tst == null ? "-" : spO2Metrics.tst}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
        <Stack gap={2}>
          <Typography sx={labelStyle}>身体情報</Typography>
          <TableContainer
            sx={{
              border: "solid #000",
              borderWidth: "1px 0px 0px 1px",
              borderRadius: 0,
            }}
          >
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={titleCellStyle}>身長</TableCell>
                  <TableCell sx={{ ...cellStyle, width: "118px" }}>
                    {examinee.height}cm
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={titleCellStyle}>体重</TableCell>
                  <TableCell sx={{ ...cellStyle, width: "118px" }}>
                    {examinee.weight}kg
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={titleCellStyle}>BMI</TableCell>
                  <TableCell sx={{ ...cellStyle, width: "118px" }}>
                    {examinee.bmi}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Stack>
      <Stack
        sx={{
          ml: "8px",
          "& .MuiTypography-root": {
            fontSize: "10px",
            fontWeight: 400,
            color: "#000",
          },
        }}
      >
        <Typography>
          ※
          ODIとは、血中酸素飽和度の降下指数です。ODI3は、SpO2が基準から3％以上降下した1時間あたりの回数を示します。
        </Typography>
        <Typography>
          ※
          SpO2とは、血中酸素飽和度で、血液中の酸素の量を示します。標準値は96～99％です。
        </Typography>
      </Stack>
    </Stack>
  );
}
